import React, { useMemo } from 'react';
import { useParams, useLocation } from 'react-router';

import { FiveHundredErrorPage } from '@sm/webassets';

import useTrackingDetails from '~pricing/hooks/useTrackingDetails';
import useStarterEnabled from '~pricing/hooks/useStarterEnabled';
import { usePricingExperience } from '~shared/hooks/usePricingExperience';
import { PackageType } from '~shared/types/pricing/package';
import { logPricingPageLoad } from '~pricing/pages/shared/index';
import { getPackagesBasedOnQueryStringIds, getPackageSetBasedOnPath } from '~pricing/lib/lib-helpers';
import { PackagesProvider } from '~shared/hooks/usePackages';
import { MatchParams } from './types';
import { usePricingExperiments } from '~shared/hooks/usePricingExperiments';
import { EXPERIMENT_IDENTIFIERS } from '~server/routes/Pricing/experimentsConfig';

export function withPageHandler<P>(WrappedComponent: React.ComponentType<P>): React.ComponentType<P> {
  return props => {
    const { canonicalPath = PackageType.Teams } = useParams<MatchParams>();
    const { search } = useLocation();
    const query = new URLSearchParams(search);
    const comparePlans = query.get('compare_plans');

    const { pageName, utSource } = useTrackingDetails();
    useMemo(() => {
      logPricingPageLoad({ pageName, utSource });
    }, [pageName, utSource]);

    const { packages } = usePricingExperience();
    const { isStarterEnabled } = useStarterEnabled();
    const { uiExperiment } = usePricingExperiments();

    // GROW-2197
    const isROWStarterAnnualEnabled =
      uiExperiment.hasAssignment && uiExperiment.treatmentName === 'grow_2197_row_starter_annual_v2_treatment';
    // END GROW-2197

    // GROW-2529 Forms, GROW-2629 v2 Forms, price_test with major UI changes
    const isForms =
      uiExperiment.hasAssignment &&
      [
        EXPERIMENT_IDENTIFIERS.GROW_2629_FORMS_V2.treatment,
        EXPERIMENT_IDENTIFIERS.GROW_2529_FORMS.treatment1,
        EXPERIMENT_IDENTIFIERS.GROW_2529_FORMS.treatment2,
      ].includes(uiExperiment.treatmentName)
        ? uiExperiment
        : null;
    // END GROW-2529, GROW-2629

    // no data is no page
    const error = !Array.isArray(packages) || !packages.length;

    return (
      <Choose>
        {/* if there was an issue loading the data from pricingsvc, we've got nothing to show. bail */}
        <When condition={error}>
          <FiveHundredErrorPage />
        </When>
        <When condition={!error && packages.length}>
          <PackagesProvider
            packagesToDisplay={
              comparePlans
                ? getPackagesBasedOnQueryStringIds(comparePlans, canonicalPath)
                : getPackageSetBasedOnPath(canonicalPath, isForms, isStarterEnabled || isROWStarterAnnualEnabled)
            }
            showEduDiscount={canonicalPath === PackageType.Educational}
            comparePlans={Boolean(comparePlans)}
          >
            <WrappedComponent {...props} />
          </PackagesProvider>
        </When>
      </Choose>
    );
  };
}

export default withPageHandler;
